import { chromeControlClass } from "../../lib/chrome-control.js";
import { SHAPE_CLASS } from "../../lib/shape.js";
import { cn } from "../../lib/utils.js";
import {
  FOCUSABLE_DISABLED_DIM,
  buttonVariants,
  type ButtonVariant,
  type ButtonVariantsOptions,
} from "./button-variants.js";

/* A Button inside a rail group (lib/control-context.ts provides "rail";
   workspace-control-group-rail-controls). The chrome-control recipe in
   lib/chrome-control.ts owns the rail and primary looks:
   - untoned ghost, secondary and outline take the rail look: a 32px pill
     with a label, a 32px circle when iconOnly;
   - untoned primary takes the recipe's 32px primary look;
   - every other variant (soft, destructive, inverse) and every toned pair
     keeps its own paint on the 32px pill box;
   - link ignores the context.
   `size` is ignored, an explicit `shape` still wins (decision 4) and
   `pressed` paints the recipe's teal selection. */

/* The 32px box a painted variant keeps: the recipe's height, padding and
   type step (the primary look's box), fully round unless a shape is set. */
const RAIL_BOX = "h-8 px-2 text-sm";
const RAIL_SQUARE = "h-8 w-8";

/* The untoned variants that take a recipe look instead of their own paint. */
const RECIPE_LOOK: Partial<Record<ButtonVariant, "rail" | "primary">> = {
  ghost: "rail",
  secondary: "rail",
  outline: "rail",
  primary: "primary",
};

/** The class list of a Button inside a rail group. */
export function railButtonClass(options: ButtonVariantsOptions): string {
  const { variant, tone, iconOnly, shape, pressed, focusableDisabled } =
    options;
  const v = variant ?? "secondary";
  if (v === "link") return buttonVariants(options);
  const square = Boolean(iconOnly) || shape === "circle";
  const look = tone ? undefined : RECIPE_LOOK[v];

  if (look) {
    return cn(
      chromeControlClass({
        look,
        shape: shape ?? undefined,
        iconOnly: square,
        pressed: pressed ?? undefined,
      }),
      focusableDisabled && FOCUSABLE_DISABLED_DIM,
    );
  }

  return cn(
    buttonVariants({ ...options, size: "md" }),
    square ? RAIL_SQUARE : RAIL_BOX,
    SHAPE_CLASS[shape ?? "pill"],
  );
}
